import React, { useEffect, useState, useCallback } from "react";
import api from "../helpers/api";

export const ASSET_SHORTCUTS = [
  {
    key: "critical_offline",
    countField: "criticalAssetsOfflineCount",
    labelKey: "business.assets.shortcut.critical_offline",
    fallback: "Critical — offline",
    accent: "#dc3545",
    alert: true,
  },
  {
    key: "critical_no_preventive",
    countField: "criticalAssetsWithoutPreventiveWoCount",
    labelKey: "business.assets.shortcut.critical_no_preventive",
    fallback: "No preventive WO",
    accent: "#111827",
  },
  {
    key: "critical_assets",
    countField: "criticalAssetsCount",
    labelKey: "business.assets.shortcut.critical",
    fallback: "Critical assets",
    accent: "#111827",
  },
  {
    key: "open_reactive_wo",
    countField: "assetsWithOpenReactiveWoCount",
    labelKey: "business.assets.shortcut.open_reactive_wo",
    fallback: "Open reactive WOs",
    accent: "#0d6efd",
  },
  {
    key: "never_maintained",
    countField: "assetsNeverMaintainedCount",
    labelKey: "business.assets.shortcut.never_maintained",
    fallback: "Never maintained",
    accent: "#111827",
  },
  {
    key: "offline",
    countField: "offlineAssetsCount",
    labelKey: "business.assets.shortcut.offline",
    fallback: "Offline",
    accent: "#111827",
  },
];

const MUTED = "#9ca3af";
const LABEL_DEFAULT = "#374151";

const AssetShortcuts = ({ organizationId, activeShortcut, onShortcutClick, t }) => {
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);

  const fetchCounts = useCallback(async () => {
    if (!organizationId) return;
    setLoading(true);
    try {
      const { data } = await api.get("dashboard");
      setCounts(data?.response || {});
    } catch (err) {
      console.error("Error fetching dashboard counts:", err);
      setCounts({});
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  return (
    <div
      className="mb-3"
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
        gap: 12,
      }}
    >
      {ASSET_SHORTCUTS.map((sc) => {
        const count = counts[sc.countField] ?? 0;
        const isActive = activeShortcut === sc.key;
        const isZero = !loading && count === 0;
        const isAlertState = sc.alert && count > 0;

        const countColor = isZero ? MUTED : sc.accent;
        const labelColor = isZero ? MUTED : LABEL_DEFAULT;

        const tileStyle = {
          position: "relative",
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "14px 16px",
          border: "1px solid #e5e7eb",
          borderRadius: 10,
          background: isZero ? "#f9fafb" : "#fff",
          cursor: "pointer",
          textAlign: "left",
          transition: "border-color 0.15s, box-shadow 0.15s, transform 0.15s",
          boxShadow: isActive ? `0 0 0 2px ${sc.accent}33` : "0 1px 2px rgba(16,24,40,0.04)",
          borderLeft: isAlertState ? `3px solid ${sc.accent}` : "1px solid #e5e7eb",
        };

        return (
          <button
            key={sc.key}
            type="button"
            style={tileStyle}
            onClick={() => onShortcutClick(isActive ? null : sc.key)}
            aria-pressed={isActive}
          >
            <div style={{ lineHeight: 1.25, minWidth: 0, flex: 1 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: 6,
                  flexWrap: "nowrap",
                  minWidth: 0,
                }}
              >
                <span style={{ fontSize: 22, fontWeight: 700, color: countColor, flexShrink: 0 }}>
                  {loading ? "—" : count}
                </span>
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: labelColor,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    minWidth: 0,
                  }}
                  title={t(sc.labelKey, sc.fallback)}
                >
                  {t(sc.labelKey, sc.fallback)}
                </span>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default AssetShortcuts;
